import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { AuthTokens } from '@financial/shared';

const baseURL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

/** Cliente HTTP tipado con refresh automático de token. */
export const api: AxiosInstance = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
});

interface AuthHandlers {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  onTokens: (tokens: AuthTokens) => void;
  onAuthFailure: () => void;
}

let handlers: AuthHandlers = {
  getAccessToken: () => null,
  getRefreshToken: () => null,
  onTokens: () => undefined,
  onAuthFailure: () => undefined,
};

/** El store de auth registra aquí cómo leer/actualizar los tokens. */
export function configureAuth(next: AuthHandlers): void {
  handlers = next;
}

api.interceptors.request.use((config) => {
  const token = handlers.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** Refresh en vuelo compartido para evitar múltiples llamadas concurrentes. */
let refreshing: Promise<AuthTokens | null> | null = null;

async function doRefresh(refreshToken: string): Promise<AuthTokens | null> {
  try {
    // axios "crudo" para no recursar por los interceptores.
    const res = await axios.post<AuthTokens>(`${baseURL}/auth/refresh`, { refreshToken });
    return res.data;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status;
    const url = original?.url ?? '';

    if (status === 401 && original && !original._retry && !url.includes('/auth/')) {
      original._retry = true;
      const refreshToken = handlers.getRefreshToken();
      if (!refreshToken) {
        handlers.onAuthFailure();
        return Promise.reject(error);
      }
      if (!refreshing) {
        refreshing = doRefresh(refreshToken).finally(() => {
          // liberamos tras resolver, ver abajo
        });
      }
      const tokens = await refreshing;
      refreshing = null;
      if (!tokens) {
        handlers.onAuthFailure();
        return Promise.reject(error);
      }
      handlers.onTokens(tokens);
      original.headers.Authorization = `Bearer ${tokens.accessToken}`;
      return api(original);
    }

    return Promise.reject(error);
  },
);
